import { EditorState, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { Editor, isTextSelection } from '@tiptap/react';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Bold } from './Bold';
import { Code } from './Code';
import { Highlight } from './Highlight';
import { Italic } from './Italic';
import { Link } from './Link';
import { LinkEditor } from './LinkEditor';
import { Subscript } from './Subscript';
import { Superscript } from './Superscript';
import { Underline } from './Underline';

//eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import { BubbleMenu } from '@tiptap/react/menus';

import classes from './InlineTools.module.scss';

const INLINE_TOOLS_PLUGIN_KEY = new PluginKey('inlineTools');

interface ShouldShowArgs {
  editor: Editor;
  element: HTMLElement;
  view: EditorView;
  state: EditorState;
  from: number;
  to: number;
}

export interface InlineToolsProps {
  editor: Editor;
  isCompact?: boolean;
}

const shouldShow = ({ editor, element, view, state, from, to }: ShouldShowArgs) =>
  (view.hasFocus() || element.contains(document.activeElement)) &&
  isTextSelection(state.selection) &&
  Boolean(state.doc.textBetween(from, to).length) &&
  !editor.isActive('table');

export const InlineTools: FunctionComponent<InlineToolsProps> = ({ editor, isCompact }) => {
  const [isLinkMode, setIsLinkMode] = useState(false);

  const bubbleMenuRef = useRef<HTMLDivElement>(null);

  const options = useMemo(
    () => ({
      shift: { padding: 8 },
      get scrollTarget() {
        return editor.view.dom.parentElement ?? window;
      },
      onHide: () => setIsLinkMode(false),
    }),
    [editor]
  );

  const openLinkHandler = useCallback(() => {
    bubbleMenuRef.current?.focus();
    setIsLinkMode(true);
  }, []);

  const closeLinkHandler = useCallback(() => {
    editor.view.focus();
    setIsLinkMode(false);
  }, [editor]);

  const content = useMemo(() => {
    if (isLinkMode) {
      return <LinkEditor editor={editor} onClose={closeLinkHandler} />;
    }

    return (
      <>
        <Bold editor={editor} />
        <Italic editor={editor} />
        <Underline editor={editor} />

        <span className={classes['inline-tools__separator']} />

        <Link editor={editor} onClick={openLinkHandler} />

        {!isCompact && (
          <>
            <Highlight editor={editor} />

            <span className={classes['inline-tools__separator']} />

            <Code editor={editor} />
            <Subscript editor={editor} />
            <Superscript editor={editor} />
          </>
        )}
      </>
    );
  }, [closeLinkHandler, editor, isCompact, isLinkMode, openLinkHandler]);

  useLayoutEffect(() => {
    editor.commands.setMeta(INLINE_TOOLS_PLUGIN_KEY, 'updatePosition');
  }, [editor, isLinkMode]);

  useEffect(() => {
    const menuElement = bubbleMenuRef.current;

    if (!menuElement) {
      return;
    }

    const editorElement = editor.view.dom;

    const editorKeyDownHandler = (event: KeyboardEvent) => {
      if (!menuElement.isConnected) {
        return;
      }

      if (event.key === 'Escape') {
        event.stopPropagation();
        editor.commands.setTextSelection(editor.state.selection.to);
        return;
      }

      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'k' &&
        !editor.isActive('code')
      ) {
        event.preventDefault();
        setIsLinkMode(true);
      }
    };

    editorElement.addEventListener('keydown', editorKeyDownHandler);

    return () => editorElement.removeEventListener('keydown', editorKeyDownHandler);
  }, [editor]);

  useEffect(() => {
    const menuElement = bubbleMenuRef.current;

    if (!menuElement) {
      return;
    }

    const editorElement = editor.view.dom;

    const menuKeyDownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.stopPropagation();
      closeLinkHandler();
    };

    const focusOutHandler = ({ relatedTarget }: FocusEvent) => {
      if (!menuElement.isConnected || !document.hasFocus()) {
        return;
      }

      if (relatedTarget instanceof Node && menuElement.contains(relatedTarget)) {
        return;
      }

      if (relatedTarget === editorElement) {
        setIsLinkMode(false);
        return;
      }

      editor.commands.setMeta(INLINE_TOOLS_PLUGIN_KEY, 'hide');
    };

    menuElement.addEventListener('keydown', menuKeyDownHandler);
    menuElement.addEventListener('focusout', focusOutHandler);
    editorElement.addEventListener('focusout', focusOutHandler);

    return () => {
      menuElement.removeEventListener('keydown', menuKeyDownHandler);
      menuElement.removeEventListener('focusout', focusOutHandler);
      editorElement.removeEventListener('focusout', focusOutHandler);
    };
  }, [closeLinkHandler, editor]);

  return (
    <BubbleMenu
      ref={bubbleMenuRef}
      editor={editor}
      pluginKey={INLINE_TOOLS_PLUGIN_KEY}
      shouldShow={shouldShow}
      options={options}
      className={classes['inline-tools']}
    >
      {content}
    </BubbleMenu>
  );
};
